import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const SystemSettingsCtx = createContext({
  settings: {}, theme: "dark", setTheme: () => {}, refresh: () => {},
  brandName: "", brandShort: "",
});

const LS_THEME_KEY = "loop.theme";

// ---- Farbhilfen für Akzent-CSS-Variable ---------------------------------
function hexToRgb(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function relativeLuminance({ r, g, b }) {
  const c = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

function applyAccent(hex, hexSecondary, hexInteractive) {
  const el = document.documentElement;
  function apply(prefix, h) {
    const rgb = hexToRgb(h);
    if (!rgb) {
      el.style.removeProperty(`--${prefix}`);
      el.style.removeProperty(`--${prefix}-fg`);
      el.style.removeProperty(`--${prefix}-soft`);
      el.style.removeProperty(`--${prefix}-strong`);
      return;
    }
    el.style.setProperty(`--${prefix}`, h);
    el.style.setProperty(
      `--${prefix}-fg`,
      relativeLuminance(rgb) > 0.5 ? "#111111" : "#ffffff"
    );
    el.style.setProperty(
      `--${prefix}-soft`,
      `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`
    );
    el.style.setProperty(
      `--${prefix}-strong`,
      `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`
    );
  }
  // Zielt bewusst auf den eigenen Namensraum `brand-accent*` — NICHT
  // `--accent`, weil das ein shadcn Design-Token ist (Component-Surface,
  // im Light-Theme hellgrau). Sonst würden Cyan-Akzente in White-Theme
  // grau werden (siehe Regression 2026-08-30 Fix).
  apply("brand-accent", hex);
  apply("brand-accent-2", hexSecondary);
  apply("brand-accent-3", hexInteractive);
}

function applyTheme(theme) {
  const el = document.documentElement;
  let effective = theme;
  if (theme === "system") {
    effective = window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  if (effective === "light") el.classList.add("light");
  else el.classList.remove("light");
  el.setAttribute("data-theme", effective);
}

export function SystemSettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [theme, setThemeState] = useState(() => localStorage.getItem(LS_THEME_KEY) || "dark");

  const setTheme = (t) => {
    setThemeState(t);
    localStorage.setItem(LS_THEME_KEY, t);
    applyTheme(t);
  };

  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => { applyAccent(settings?.accent_color, settings?.accent_color_secondary, settings?.accent_color_interactive); }, [settings?.accent_color, settings?.accent_color_secondary, settings?.accent_color_interactive]);

  const refresh = async () => {
    try {
      const r = await api.get("/system-settings");
      setSettings(r.data || {});
      const localPref = localStorage.getItem(LS_THEME_KEY);
      if (!localPref && r.data?.theme_default) setThemeState(r.data.theme_default);
    } catch { setSettings({}); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // Zentrale Branding-Helper.
  //
  // ``brandName``  → voller Anzeigename aus ``system_settings.company_name``.
  //                  Bewusst KEIN hartcodierter Marken-Fallback: solange der
  //                  Wert leer ist, zeigen konsumierende Komponenten entweder
  //                  das Logo oder gar nichts an (nie einen fremden Namen).
  // ``brandShort`` → wird für Labels wie „<Name> Punkte" verwendet und
  //                  entspricht bewusst dem VOLLEN Namen. Ein Alias-Feld
  //                  bleibt bestehen für die Fälle, in denen wir später
  //                  wirklich eine Kurzform brauchen (dann via Settings-Key
  //                  ``brand_short``); im Moment ist die Kurzform = Name.
  const brandName = (settings?.company_name?.trim() || "");
  const brandShort = brandName;

  // Zentrale Feature-Flag-Ableitung (V1 Core Completion).
  // Werte kommen aus ``system_settings`` (``"1"`` = an, sonst aus).
  // Default OFF: solange kein Admin explizit einschaltet, sind
  // Punkte- + Club-Card-UI verborgen.
  const featurePoints    = settings?.points_enabled === "1";
  const featureClubCard  = settings?.club_card_enabled === "1";

  const value = useMemo(
    () => ({ settings, theme, setTheme, refresh, brandName, brandShort,
             featurePoints, featureClubCard }),
    [settings, theme, brandName, brandShort, featurePoints, featureClubCard]
  );
  return <SystemSettingsCtx.Provider value={value}>{children}</SystemSettingsCtx.Provider>;
}

export function useSystemSettings() { return useContext(SystemSettingsCtx); }

// Zentraler Hook zum Guarden einzelner Feature-Bereiche.
// Verwendung: ``const on = useFeatureEnabled("points")`` bzw. ``"club_card"``.
export function useFeatureEnabled(name) {
  const { featurePoints, featureClubCard } = useContext(SystemSettingsCtx);
  if (name === "points") return featurePoints;
  if (name === "club_card") return featureClubCard;
  return false;
}
